import React, { useState, useEffect } from "react";
import {
  getMostCommonTopicYearly,
  getYearlyTopicPercentage,
  getMonthlyThematicEvolution,
} from "../../../services/singleMonths";
import { getCountryColorList } from "../../../utils/functions/colors";
import { convTransformation as texts_es } from "../../../utils/text/graphTexts";
import { convTransformation as texts_en } from "../../../utils/text/graphTexts_en";
import { generateMultiplLineTooltip } from "../../../utils/functions/generateLineTooltip";
import { useParams } from "react-router-dom";
import {
  COUNTRY_OPTIONS,
  getCountriesAsSelectOptions,
} from "../../../utils/constants/countryOptions";
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import { getThematicEvolutionData } from "../../../services/dashboard";
import { getDateString } from "../../../utils/functions/getDateString";
import {
  GRID_CONFIG_WITH_LEGEND,
  LEGEND_CONFIG,
  TOOLTIP_STYLE,
} from "../../../utils/constants/graphConfigs";
import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { seriesValues } from "../../../utils/functions/seriesValues";
import { transformNameCountries } from "../../../utils/functions/transformNameCountries";
import { getMonthText } from "../../../utils/functions/getMonthText";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { isCountryIncluded } from "../../../utils/functions/isCountryIncluded";
import { isTopicIncluded } from "../../../utils/functions/isTopicIncluded";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";

export default function ConversationTransformation({
  selectedCountry,
  colorRBG,
}) {
  const { country } = useParams();
  const { dateOptions, topicOptions, errorText } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("evolution");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();

  const [selectedYear, setSelectedYear] = useState("HIST");
  const [lastMonthData, setLastMonthData] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);

  // Unique to Tweets component
  // Never send unique states to Card components

  const [selectedTopic, setSelectedTopic] = useState();

  // Country Select
  const [countrySelectConfig, setCountrySelectConfig] = useState({
    value: ["LAC"],
    options: getCountriesAsSelectOptions(language),
  });

  // Topic Select
  const [topicSelectConfig, setTopicSelectConfig] = useState({
    value: [],
    options: [],
  });

  const handleTopicChange = (newSelectedTopic) => {
    setSelectedTopic(newSelectedTopic);
  };

  const createDateString = () => {
    let dateString;

    try {
      dateString = isMostRecentYear
        ? getDateString(
            language,
            dateOptions.mostRecentDate.year,
            dateOptions.mostRecentDate.month
          )
        : getDateString(
            language,
            selectedYear === "HIST"
              ? dateOptions.mostRecentDate.year
              : selectedYear
          );
    } catch (error) {
      dateString = "-----";
    }

    return dateString;
  };

  function handleCountrySelectChange(newValue) {
    setCountrySelectConfig({
      ...countrySelectConfig,
      value: newValue,
    });
  }

  function handleTopicMultipleChange(newValue) {
    setTopicSelectConfig({
      ...topicSelectConfig,
      value: newValue,
    });
  }

  // Useeffect that collents yead and month data
  useEffect(() => {
    if (dateOptions && topicOptions) {
      setSelectedYear("HIST");
      setSelectedTopic(topicOptions[0].code);
      setIsLoadingInitialData(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, topicOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    async function updateData() {
      try {
        const response = await getThematicEvolutionData(
          currentCountry,
          selectedTopic,
          selectedYear
        );

        if (response) {
          let transformedData = {};
          if (currentCountry !== "LAC") {
            let realName;
            Object.keys(response.data).forEach((topic) => {
              if (topic === "dates") {
                transformedData["dates"] = response.data["dates"].map((date) =>
                  getMonthText(date, language, false)
                );
                return;
              }
              realName = topicOptions.find((to) => to.code === topic).label;
              transformedData[realName] = response.data[topic];
            });
          } else {
            transformedData = transformNameCountries(response.data, language);
          }

          let countries = Object.keys(transformedData);
          countries.shift();

          let series = seriesValues(transformedData);

          countries = countries.filter((c) =>
            currentCountry === "LAC"
              ? isCountryIncluded(c, countrySelectConfig.value, language)
              : isTopicIncluded(
                  c,
                  topicSelectConfig.options,
                  topicSelectConfig.value
                )
          );

          series = series.filter((line) =>
            currentCountry === "LAC"
              ? isCountryIncluded(
                  line.name,
                  countrySelectConfig.value,
                  language
                )
              : isTopicIncluded(
                  line.name,
                  topicSelectConfig.options,
                  topicSelectConfig.value
                )
          );

          const options = {
            color:
              currentCountry === "LAC"
                ? getCountryColorList(countries, colorRBG)
                : [
                    "#305EA9",
                    "#308144",
                    "#1C97C9",
                    "#B1B6BA",
                    "#555555",
                    "#24264F",
                    "#8dba38",
                    "#6CCDEF",
                    "#F4DB4E",
                    "#4fb96a",
                  ],
            backgroundColor: "transparent",
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                const isPercentage = true;
                const areColorsSwitched = false;
                return generateMultiplLineTooltip(
                  params,
                  transformedData,
                  isPercentage,
                  areColorsSwitched,
                  language
                );
              },
            },
            grid: GRID_CONFIG_WITH_LEGEND,
            legend: {
              ...LEGEND_CONFIG,
              data: countries,
              selectedMode: false,
            },
            xAxis: {
              type: "category",
              data: transformedData.dates,
              axisLabel: {
                showMaxLabel: true,
                margin: 10,
                interval: 2
              },
            },
            yAxis: {
              type: "value",
              axisLine: {
                show: true,
              },
              axisLabel: {
                margin: 20,
                fontSize: 14,
                formatter: "{value}%",
              },
            },

            series: series,
          };

          setOptions(options);
          setIsDataAvailable(true);
          setIsLoading(false);
          setErrorLoadingGraph(false);
        }
      } catch (error) {
        setOptions(null);
        setIsDataAvailable(false);
        setErrorLoadingGraph(true);
        console.log(error);
      }
    }

    if (!isLoadingInitialData && dateOptions && selectedYear && selectedTopic)
      updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateOptions,
    selectedYear,
    selectedTopic,
    isLoadingInitialData,
    currentCountry,
    countrySelectConfig,
    topicSelectConfig,
    language,
  ]);

  // Update last month data
  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      setLastMonthData(null);

      let promises, callback;

      if (isMostRecentYear) {
        const monthlyTematicEvolutionPromise = getMonthlyThematicEvolution(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        promises = [monthlyTematicEvolutionPromise];
        callback = (results) => {
          let result, maxValue, maxTopic;

          if (currentCountry === "LAC") {
            result = results[0].data[selectedTopic].toFixed(1) + "%";
          } else {
            const topics = Object.keys(results[0].data);
            maxValue = results[0].data[topics[0]];
            maxTopic = topics[0];

            topics.forEach((t) => {
              if (results[0].data[t] > maxValue) {
                maxValue = results[0].data[t];
                maxTopic = t;
              }
            });

            result =
              topicOptions.find((topic) => topic.code === maxTopic).label +
              ": " +
              maxValue.toFixed(1) +
              "%";
          }
          setLastMonthData(result);
        };
      } else {
        if (currentCountry === "LAC") {
          const topicYearPercentagePromise = getYearlyTopicPercentage(
            currentCountry,
            selectedYear,
            selectedTopic
          );

          promises = [topicYearPercentagePromise];
          callback = (results) => {
            setLastMonthData(results[0].data.value.toFixed(1) + "%");
          };
        } else {
          const yearDataPromise = getMostCommonTopicYearly(
            currentCountry,
            selectedYear
          );

          promises = [yearDataPromise];
          callback = (results) => {
            setLastMonthData(
              topicOptions.find((topic) => topic.code === results[0].data.topic)
                .label +
                ": " +
                results[0].data.topic_percentage.toFixed(1) +
                "%"
            );
          };
        }
      }

      Promise.all(promises)
        .then(callback)
        .catch(() => {
          setLastMonthData("--");
          setIsLoading(false);
        });
    };

    if (dateOptions && selectedTopic && selectedYear && currentCountry)
      getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, currentCountry, selectedYear, selectedTopic]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountrySelectConfig({
        ...countrySelectConfig,
        isIncluded: selectedCountry === "LAC",
      });
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  useEffect(() => {
    if (topicOptions && topicOptions !== errorText) {
      setTopicSelectConfig({
        value: ["migrant_visibility", "migratory_policy", "security"],
        options: topicOptions.map((topic) => {
          return {
            value: topic.code,
            label: topic.label,
          };
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicOptions]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
    if (currentCountry === "LAC") {
      setCountrySelectConfig({
        ...countrySelectConfig,
        options: getCountriesAsSelectOptions(language),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  function TransformationContent() {
    let topic, content;
    if (currentCountry === "LAC" && topicOptions && topicOptions !== errorText)
      topic = topicOptions.find((topic) => topic.code === selectedTopic);

    try {
      if (dateOptions === errorText || topicOptions === errorText) throw Error;
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={lastMonthData}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology}
          dataSubtitle={texts.dataSubtitle(
            currentCountry,
            selectedTopic,
            topicOptions
          )}
          description={texts.description()}
          dataDate={createDateString()}
          selectArray={
            currentCountry !== "LAC"
              ? [
                  {
                    selectedValue: selectedYear,
                    changeHandler: (newSelectedYear) =>
                      handleYearChange(
                        newSelectedYear,
                        setIsMostRecentYear,
                        setSelectedYear,
                        dateOptions
                      ),
                    options: dateOptions.yearOptions,
                    placeHolderText: miscText["yearPlaceholder"][language],
                    labelAttributeName: "years_list",
                    valueAttributeName: "years_code",
                  },
                ]
              : [
                  {
                    selectedValue: selectedYear,
                    changeHandler: (newSelectedYear) =>
                      handleYearChange(
                        newSelectedYear,
                        setIsMostRecentYear,
                        setSelectedYear,
                        dateOptions
                      ),
                    options: dateOptions.yearOptions,
                    placeHolderText: miscText["yearPlaceholder"][language],
                    labelAttributeName: "years_list",
                    valueAttributeName: "years_code",
                  },
                  {
                    selectedValue: selectedTopic,
                    changeHandler: handleTopicChange,
                    options: topicOptions,
                    placeHolderText: miscText["topicPlaceholder"][language],
                    labelAttributeName: "label",
                    valueAttributeName: "code",
                    ignoreTranslation: true,
                  },
                ]
          }
          language={language}
          includesGlossary={true}
          includesCountrySelect={true}
          handleCountrySelectChange={
            currentCountry === "LAC"
              ? handleCountrySelectChange
              : handleTopicMultipleChange
          }
          countrySelectDefaultValue={
            currentCountry === "LAC"
              ? countrySelectConfig.value
              : topicSelectConfig.value
          }
          countrySelectOptions={
            currentCountry === "LAC"
              ? countrySelectConfig.options
              : topicSelectConfig.options
          }
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es"
              ? "TransformacionDeLaConversacion"
              : "ConversationEvolution",
            currentCountry,
            selectedYear
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language,
            currentCountry === "LAC" ? topic.label : null
          )}
          downloadableCardContainsLegend={true}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology}
          dataSubtitle={null}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <TransformationContent />}
    </CardContainer>
  );
}
